import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel"
import { Box, IconButton, Tooltip } from "@mui/material"
import { red } from "@mui/material/colors";
import { FilterAlt } from "@mui/icons-material";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { cloneDeep, isEmpty, isUndefined, isEqual, isNull, size } from "lodash";
import { format } from "date-fns";

import SplitterContainer from "@plannel/components/SplitterContainer";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import rpPlanVsActualService from "@plannel/services/rp/rp-plan-vs-actual-service"
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import BucketToggleButton from "@plannel/components/BucketToggleButton"
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { AbcXyzChipInline } from "@plannel/components/aggrid/AbcXyzChip";
import numberUtil from "@plannel/utils/number-util";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { FilterButton } from "@plannel/components/ActionIconButton";
import { RPFilterModal } from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import companyService from "@plannel/services/data/company-service";
import planConfigService from "@plannel/services/data/plan-config-service";

const { PLAN_ORDER_QTY, PLAN_ARRIVAL_QTY, ACTUAL_ARRIVAL_QTY, ARRIVAL_QTY_GAP, ARRIVAL_QTY_GAP_PCT } = CodeUtil.rpMeasure;
const { DAY, WEEK, MONTH } = CodeUtil.buckets;
const { RELEASED } = CodeUtil.versionStatusCode;
const { muicolor, filterType, item, dpABCConfig, toggleType: { QTY } } = CodeUtil;
const { colorSet, snackBarMessageColor } = ColorUtil;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;

const RpPlanVsActual = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [30, 50, 100];
  const resultTotalCount = useRef(null);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { originLocation: null, destLocation: null, category: null });
  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [treeload, setTreeload] = useState(false);
  const [onlyExceptionApplied, setOnlyExceptionApplied] = useState(reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "onlyExceptionApplied") ? reduxViewState.onlyExceptionApplied : false);
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);

  const hasAdvancedFilterValue = filters => !!(filters?.advancedFilter?.children?.length > 0 || filters?.itemLevelCd);
  const [advancedFilters, setAdvancedFilters] = useState(hasAdvancedFilterValue(reduxViewState?.advancedFilters) ? reduxViewState.advancedFilters : null);
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(null);
  const [isAFActive, setIsAFActive] = useState(hasAdvancedFilterValue(reduxViewState?.advancedFilters));
  const [itemGrpLevel, setItemGrpLevel] = useState(reduxViewState?.grpLevelList ?? []);
  const [attrConfig, setAttrConfig] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);

  const [filterData, setFilterData] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const bucketTypes = [DAY, WEEK, MONTH];
  const [availableBuckets, setAvailableBuckets] = useState(bucketTypes);

  const [bucketType, setBucketType] = useState(reduxViewState?.bucketType ?? bucketTypes[2]);

  const [sortParams, setSortParams] = useState({});
  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(() => [PLAN_ORDER_QTY, PLAN_ARRIVAL_QTY, ACTUAL_ARRIVAL_QTY, ARRIVAL_QTY_GAP, ARRIVAL_QTY_GAP_PCT], []);
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);
  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);

  const [planAndActual, setPlanAndActual] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [originHeader, setOriginHeader] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [visibleColumns, setVisibleColumns] = useState([]);

  const gridRef = useRef();
  const columnDefs = useRef();
  const initSelectionDoneRef = useRef(false);
  const [gridLoading, setGridLoading] = useState(false);

  const EXCEPTION_FILTER_OPTION = ["exceptionsOnly"];

  const locationRenderer = params => {
    if (!params.data) {
      return null;
    }

    let fieldName = "";
    if (params?.column?.colId) {
      if (params.column.colId === "originLocDesc") {
        fieldName = "originLocNm"
      } else if (params.column.colId === "destLocDesc") {
        fieldName = "destLocNm"
      }
    }

    const currentValue = params.data[fieldName];
    const previousRowNode = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1);

    if (previousRowNode) {
      const previousData = previousRowNode.data;
      if (previousData && previousData[fieldName] === currentValue) {
        return null;
      }
    }
    return params.value;
  };

  const setColumnsDefs = () => {
    const defaultColumnDefs = [
      {
        headerName: 'originLocation', colId: "originLocNmGrp", width: 140, showRowGroup: 'originLocNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      },
      { headerName: "originLocName", field: "originLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "originLocDesc", field: "originLocDesc", width: 140, pinned: "left", lockPinned: true, hide: true, cellRenderer: locationRenderer },
      {
        headerName: 'destLocation', colId: "destLocNmGrp", width: 140, showRowGroup: 'destLocNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      },
      { headerName: "destLocName", field: "destLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "destLocDesc", field: "destLocDesc", width: 140, pinned: "left", lockPinned: true, hide: true, cellRenderer: locationRenderer },
      {
        headerName: !isEmpty(filterValue?.category) ? 'categoryCd' : 'itemCd', colId: "itemCdGrp", width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      },
      { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true, cellClass: 'stringType' },
      {
        headerName: !isEmpty(filterValue?.category) ? 'categoryName' : 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true
        },
      },
      { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      {
        headerName: "measure", field: "measure", width: 160, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
        valueFormatter: params => t(params.value), headerComponent: CustomHeaderMenuIcon,
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      }
    ];

    if (isUndefined(filterValue?.category?.hrchyCd)) {
      const abcXyzColDefs = [
        {
          headerName: "invClassCd",
          colId: "invClassCdGrpPlanVsActual",
          showRowGroup: "invClassCd",
          pinned: "left", lockPinned: true,
          cellRenderer: "agGroupCellRenderer",
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: AbcXyzChipInline,
          },
          width: 130,
          sortable: true,
        },
        {
          headerName: "invClassCd",
          field: "invClassCd",
          editable: false,
          pinned: "left", lockPinned: true,
          hide: true,
          suppressColumnsToolPanel: true,
          cellClass: "stringType",
          rowGroup: true,
        }
      ]
      defaultColumnDefs.push(...abcXyzColDefs);
    }

    return defaultColumnDefs;
  }

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: setColumnsDefs() });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const advancedFilterColumnDefs = [
    { headerName: "originLocation", filterType: "string" },
    { headerName: "destLocation", filterType: "string" },
    { headerName: "categoryCd", filterType: "string" },
    { headerName: "categoryName", filterType: "string" },
    { headerName: "itemCd", filterType: "string" },
    { headerName: "itemName", filterType: "string" },
    { headerName: "invClassCd", filterType: "string", filterOption: dpABCConfig },
    ...Array.from({ length: 10 }, (_, idx) => {
      const order = idx + 1;
      const attrCd = `attr${String(order).padStart(2, "0")}`;
      return {
        headerName: attrConfig?.item?.[attrCd] ?? `attr${order}`,
        filterType: "string",
        filterName: attrCd,
      };
    }),
    { headerName: "excludeCondition", filterType: "select", filterOption: EXCEPTION_FILTER_OPTION },
  ];

  /* Item Search(RPFilterModal) 의 filterList 키 → AF headerName. 여기 없는 키는 AF 로 표현할 수 없다. */
  const ITEM_SEARCH_TO_AF = {
    itemCd: "itemCd",
    name: "itemName",
    itemHrchy: "categoryName",
    invClassCd: "invClassCd",
    ...Object.fromEntries(Array.from({ length: 10 }, (_, idx) => {
      const attrCd = `attr${String(idx + 1).padStart(2, "0")}`;
      return [attrCd, attrCd];
    })),
  };

  const buildInitialFromCurrentFilters = () => {
    const nodes = [];

    const pushTo = (target, headerName, values, operator, logicOperator, options) => {
      const targets = (values ?? []).filter(Boolean);
      const leaf = value => ({ ...options, filterType: options?.filterType ?? "string", headerName, operator, values: [value] });

      if (targets.length > 1) {
        target.push({ logicOperator, children: targets.map(leaf) });
      } else if (targets.length === 1) {
        target.push(leaf(targets[0]));
      }
    };
    const pushEquals = (headerName, values, options) => pushTo(nodes, headerName, values, "equals", "OR", options);

    pushEquals("originLocation", filterValue?.originLocation?.map(location => location?.name));
    pushEquals("destLocation", filterValue?.destLocation?.map(location => location?.name));
    pushEquals("categoryName", filterValue?.category?.map(category => category?.name));
    pushEquals("invClassCd", invClassCdList, { filterOption: dpABCConfig });

    const [itemLogic, itemCompare] = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
    const itemOperator = itemCompare === filterType.notEqual.cd ? "notEqual" : "equals";
    const itemLogicOperator = itemLogic === filterType.and.cd ? "AND" : "OR";
    const unsupportedItemKeys = [];
    /* itemFilterType 의 논리 연산자는 값 사이뿐 아니라 필드 사이에도 적용된다(makeQueryCondition 과 동일). */
    const itemNodes = [];

    for (const [key, values] of Object.entries(itemFilterList ?? {})) {
      if (isEmpty(values)) {
        continue;
      }
      const headerName = ITEM_SEARCH_TO_AF[key];
      if (!headerName) {
        unsupportedItemKeys.push(key);
        continue;
      }
      pushTo(itemNodes, headerName, values, itemOperator, itemLogicOperator, key === "invClassCd" ? { filterOption: dpABCConfig } : undefined);
    }

    if (itemNodes.length > 1) {
      nodes.push({ logicOperator: itemLogicOperator, children: itemNodes });
    } else if (itemNodes.length === 1) {
      nodes.push(itemNodes[0]);
    }

    if (onlyExceptionApplied) {
      nodes.push({ filterType: "select", headerName: "excludeCondition", filterOption: EXCEPTION_FILTER_OPTION, operator: "equals", values: ["exceptionsOnly"] });
    }

    const advancedFilter = nodes.length > 0 ? { logicOperator: "AND", children: [{ logicOperator: "AND", children: nodes }] } : { logicOperator: "AND", children: [] };

    const leafLevelCd = itemGrpLevel?.[0]?.cd;
    const isCategorySelected = filterValue?.category?.length > 0 && leafLevelCd;

    return { advancedFilter, itemLevelCd: isCategorySelected ? leafLevelCd : item.cd, unsupportedItemKeys };
  };

  const itemHrchyClearValue = useMemo(() => (isAFActive ? [] : undefined), [isAFActive]);

  const handleFilterOpen = () => {
    if (!openFilter) {
      const initial = isAFActive ? null : buildInitialFromCurrentFilters();
      setPendingAdvancedFilters(initial);
      
      if (initial?.unsupportedItemKeys?.length > 0) {
        const fields = initial.unsupportedItemKeys.map(key => t(key)).join(", ");
        setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_UnsupportedItemFilterInAdvancedFilter", { fields }) });
      }
    }
    setOpenFilter(prev => !prev);
  };

  const handleAdvancedFilterApply = (filters) => {
    // AdvancedFilter는 Search Level 등이 기본값이 아닐 때만 wrapper를 주고, 아니면 트리 자체를 준다.
    const advancedFilter = filters?.advancedFilter ?? filters;
    const itemLevelCd = filters?.itemLevelCd ?? null;
    const normalized = (advancedFilter?.children?.length > 0 || itemLevelCd) ? { advancedFilter, itemLevelCd } : null;
    const clearedFilterValue = { originLocation: null, destLocation: null, category: null };

    setFilterValue(clearedFilterValue);
    setItemFilterList({});
    setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    setInvClassCdList([]);
    setOnlyExceptionApplied(false);
    setAdvancedFilters(normalized);
    setPendingAdvancedFilters(normalized);
    setIsAFActive(!!normalized);
    resultTotalCount.current = null;

    const reduxState = reduxUtil.createObjectState(viewName, { itemFilterList: {}, itemFilterType: [filterType.or.cd, filterType.equal.cd], invClassCdList: [], onlyExceptionApplied: false, advancedFilters: normalized });
    const filterState = reduxUtil.createFilterViewState(viewName, clearedFilterValue);
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
  };

  const resetAdvancedFilter = () => {
    if (isAFActive) {
      setIsAFActive(false);
    }
    setPendingAdvancedFilters({ logicOperator: "AND", children: [] });
    setAdvancedFilters(null);
    setOpenFilter(false);
  };

  const handleFilterValueChange = (newValue) => {
    if (isAFActive) {
      if (!(newValue?.originLocation?.length > 0 || newValue?.destLocation?.length > 0 || newValue?.category?.length > 0)) {
        return;
      }
      resetAdvancedFilter();
    }
    if (!isEmpty(newValue?.category) && !isEmpty(itemFilterList)) {
      setItemFilterList({});
      setItemFilterType([filterType.or.cd, filterType.equal.cd]);
      reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { itemFilterList: {}, itemFilterType: [filterType.or.cd, filterType.equal.cd] })));
    }
    setFilterValue(newValue);
  };

  const handleInvClassCdListChange = (newValue) => {
    if (isAFActive) {
      if (!(newValue?.length > 0)) {
        return;
      }
      resetAdvancedFilter();
    }
    setInvClassCdList(newValue);
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (!isEmpty(rpVersionFilter)) {
      params.versionId = rpVersionFilter.id;
    }

    if (!isAFActive) {
      if (filterValue?.originLocation) {
        params.originLocCd = filterValue?.originLocation?.map(location => location?.locCd);
      }

      if (filterValue?.destLocation) {
        params.destLocCd = filterValue?.destLocation?.map(location => location?.locCd);
      }

      if (!isEmpty(filterValue?.category)) {
        params.categoryCds = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
      } else {
        if (itemFilterList) {
          params.itemFilterList = itemFilterList;
          params.itemFilterType = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
        }
      }

      if (!isEmpty(invClassCdList)) {
        params.invClassCdList = invClassCdList;
      }

      params.onlyExceptionApplied = onlyExceptionApplied;
    }

    if (isAFActive && !isEmpty(advancedFilters)) {
      if (advancedFilters?.itemLevelCd) {
        params.itemLevelCd = advancedFilters.itemLevelCd;
      }
      if (advancedFilters?.advancedFilter?.children?.length > 0) {
        params.advancedFilters = advancedFilters.advancedFilter;
      }
    }

    params.bucketType = bucketType ?? CodeUtil.buckets.MONTH;

    if (isPagination) {
      params.page = currentPage ? currentPage - 1 : 0;
      params.pageSize = pageSize ?? 30;
    }

    params.totalCount = resultTotalCount.current ?? 0;
    params.pagination = isPagination;

    return { ...params, ...sortParams };
  }

  const getDateExceptionByBucket = headerDate => {
    const current = new ZDate();

    if (isEqual(bucketType, WEEK)) {
      // current date = 2022-10-07, 2022-10-03 ~ 2022-10-09 bucket 까지
      const nextDate = new ZDate(format(current, "yyyy-MM-dd"));
      const day = nextDate.getDay();
      const nextMonday = nextDate.getDate() - day + ((day === 0 ? 1 : 8) + 0);
      nextDate.setDate(nextMonday);
      return headerDate < nextDate;
    }

    headerDate.setDate(1);
    const currentDate = new ZDate(format(current, "yyyy-MM-dd"));
    // current date = 2022-10-07, 2022-10-01 ~ 2022-10-31 bucket 까지
    return headerDate < currentDate;
  }

  const dataCellStyle = params => {
    const { data, value, colDef } = params;
    const cellStyle = {};

    if (data && colDef && !isNull(value)) {
      const { measure } = data;
      const numValue = Number(value);
      const headerDate = new ZDate(colDef.field);
      const dateException = getDateExceptionByBucket(headerDate);
      if (isEqual(measure, ARRIVAL_QTY_GAP) && (Math.abs(numValue) > 0) && dateException) {
        cellStyle.background = red[100];
        cellStyle.fontStyle = 'italic';
      }
    }

    return cellStyle;
  }

  const setGridHeaders = (headers, headerMap) => {
    if (headers.length <= 0) {
      return;
    }

    const measureValueFormatter = (params) => {
      const data = params?.data;
      const value = params?.value;
      const measure = data?.measure;

      if (isNull(value) || isUndefined(value) || !isFinite(value)) {
        return "";
      }

      if (measure === ARRIVAL_QTY_GAP_PCT) {
        return numberUtil.formatNumberData(params, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 0 })
      }

      return numberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    }

    const colDefs = cloneDeep(setColumnsDefs());

    headers.forEach(header => {
      colDefs.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        valueFormatter: measureValueFormatter,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + header,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellStyle: dataCellStyle
      });

    });

    columnDefs.current = colDefs;
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: colDefs, alignOrderToDefs: true }); // 버킷 토글(D/W/M) 변경시 마다 날짜 컬럼 순서 보장
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const planOrderQtyPivotData = "planOrderQtyPivotData";
    const planArrivalQtyPivotData = "planArrivalQtyPivotData";
    const actualArrivalQtyPivotData = "actualArrivalQtyPivotData";
    const arrivalQtyGapPivotData = "arrivalQtyGapPivotData";
    const arrivalQtyGapPctPivotData = "arrivalQtyGapPctPivotData";
    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;
      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = { ...resultsData, measure: display != null && !isEmpty(display) ? display : measureName, originMeasureName: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case PLAN_ORDER_QTY:
              data[header] = resultsData[planOrderQtyPivotData][idx];
              break;

            case PLAN_ARRIVAL_QTY:
              data[header] = resultsData[planArrivalQtyPivotData][idx];
              break;

            case ACTUAL_ARRIVAL_QTY:
              data[header] = resultsData[actualArrivalQtyPivotData][idx];
              break;

            case ARRIVAL_QTY_GAP:
              data[header] = resultsData[arrivalQtyGapPivotData][idx];
              break;

            case ARRIVAL_QTY_GAP_PCT:
              if (resultsData[planArrivalQtyPivotData][idx] === 0 && resultsData[actualArrivalQtyPivotData][idx] !== 0) {
                data[header] = null;
              } else {
                data[header] = resultsData[arrivalQtyGapPctPivotData][idx];
              }
              break;

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retreiveConfig = (isReload = false) => {
    const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;

    if (bucketUnit === DAY) {
      setAvailableBuckets([DAY, WEEK, MONTH]);
    } else if (bucketUnit === WEEK) {
      setAvailableBuckets([WEEK, MONTH]);
    } else {
      setAvailableBuckets([MONTH]);
    }
    if (isReload) {
    setBucketType(bucketUnit);
    setReload(true);
    }
  }

  const retrievePlanAndActual = () => {
    initSelectionDoneRef.current = false;
    retreiveConfig(false);
    setSnackInfo({ ...snackInfo, open: false });
    gridRef.current?.api?.setGridOption('loading', true);

    columnDefs.current = setColumnsDefs();

    const params = getRequestParams();
    rpPlanVsActualService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;

        if (data && !isEmpty(data)) {
          setGridHeaders(localDateHeader, headerMap);
          setPlanAndActual(setGridDatas(localDateHeader, data));
          setVisibleColumns(GridUtils.getVisibleColumns(gridRef));
          setOriginHeader(localDateHeader);
          setHeaderMap(headerMap);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setPlanAndActual([]);
          // setChartData([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        const { message } = e.response?.data;
        if (message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t(message) });
        }
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemFilterType, itemFilterList, bucketType, invClassCdList, currentPage, pageSize, displayName, grpLevelList: itemGrpLevel, advancedFilters: isAFActive ? advancedFilters : null });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
        setTreeload(false);
        setReload(false);
      });
  }

  const setChart = (selectedRows) => {
    if (isEmpty(selectedRows) || (selectedRows && selectedRows.length <= 0)) {
      setChartData([]);
      return;
    }

    const rowList = [];
    for (const row of selectedRows) {
      // if (!includes(row.measure, "Per")) {
        rowList.push(row);
      // }
    }
    const chartDataList = [];
    for (let idx = 0; idx < originHeader.length; idx++) {
    // for (const header in originHeader) {
      const cData = {};
      for (const row of rowList) {
        if (size(row) > 0) {
          const display = displayName !== null && displayName[ARRIVAL_QTY_GAP_PCT] !== null
            ? displayName[ARRIVAL_QTY_GAP_PCT] : ARRIVAL_QTY_GAP_PCT;
          if (row.measure === display) {
            cData[row.originMeasureName] = row[[originHeader[idx]]] * 100;
          } else {
            cData[row.originMeasureName] = row[[originHeader[idx]]];
          }
        }
      }
      // cData.yyyymmdd = getFormatDate(idx);
      cData.yyyymmdd = headerMap[originHeader[idx]];
      chartDataList.push(cData);
    }
    setChartData(chartDataList);
  }

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: setColumnsDefs(), viewName, initState: true });
  }

  const onColumnVisible = () => {
    const updatedVisibleColumns = GridUtils.getVisibleColumns(gridRef);
    setVisibleColumns(updatedVisibleColumns);
  }

  const retrieveP13n = () => {
    return planConfigService.getMeasureDisplayNames(CodeUtil.module.RP)
      .then(response => {
        const { data } = response;
        if (data && !isEmpty(data?.objVal)) {
          setDisplayName(data.objVal);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setGridLoading(true)
      });
  }

  const retrieveCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        setAttrConfig(response.data ?? {});
      })
      .catch(e => {
        setAttrConfig({});
        debug(e);
      });
  }

  const retrieveHrchyGroup = () => {
    hrchyConfigService.getAll()
      .then(response => {
        if (response.data) {
          const { results } = response.data;
          const itemLevels = (results ?? []).filter(level => level.itemHrchyFlg && level.hrchyNum === CodeUtil.hrchyNum.HRCHY);
          const leafLevel = itemLevels.reduce((deepest, level) => (deepest === null || level.seqNum > deepest.seqNum ? level : deepest), null);
          setItemGrpLevel(leafLevel ? [leafLevel] : []);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveExportColumns = () => {
    return visibleColumns.map(column => ({
      headerName: column.headerName,
      field: column.showRowGroup || column.field,
      cellClass: column.showRowGroup || column.field === "ItemCd" ? "stringType" : null,
      valueFormatter: column.field === "measure" ? params => t(params.value) : null,
    }));
  }

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  };

  const selectTopRowGroup = api => {
    const displayedRowCount = typeof api?.getDisplayedRowCount === "function" ? api.getDisplayedRowCount() : 0;
    if (displayedRowCount < 1 || typeof api?.getDisplayedRowAtIndex !== "function") {
      setChartData([]);
      return;
    }

    const firstDisplayedNode = api.getDisplayedRowAtIndex(0);
    const firstLeafNode = firstDisplayedNode?.allLeafChildren?.[0]
      ?? firstDisplayedNode?.parent?.allLeafChildren?.[0]
      ?? firstDisplayedNode;

    if (!firstLeafNode?.data) {
      setChartData([]);
      return;
    }

    const leafChildren = firstLeafNode.parent?.allLeafChildren?.length
      ? firstLeafNode.parent.allLeafChildren
      : [firstLeafNode];
    const selectedNodes = typeof api?.getSelectedNodes === "function" ? api.getSelectedNodes() : [];

    if (selectedNodes.length > 0) {
      api.setNodesSelected({ nodes: selectedNodes, newValue: false });
    }

    api.setNodesSelected({ nodes: leafChildren, newValue: true, source: CHECKBOX_SELECTED });
    api.ensureIndexVisible(firstLeafNode.rowIndex, "top");
    setChart(leafChildren.map(leaf => leaf.data).filter(Boolean));
  };

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const handleAlignment = (_, newAlignment) => {
    if (!isNull(newAlignment)) {
      setBucketType(newAlignment);
      setReload(true);
    }
  };

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrievePlanAndActual();
    }
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planAndActual, viewName]);


  useEffect(() => {
    if (filterData.filterList) {
      if (isAFActive) {
        resetAdvancedFilter();
      }
      const filter = { ...filterValue, category: [] };
      if (!isEmpty(filterValue?.category)) {
        setTreeload(true);
      }
      setFilterValue(filter);
      setItemFilterList(filterData.filterList ?? {});
      setItemFilterType(filterData.filterType)
      if (reduxViewState) {
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, filter, itemFilterType: filterData?.filterType, itemFilterList: filterData?.filterList }));
      }

      setReload(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (itemFilterList && Object.keys(itemFilterList)?.length > 0) {
      setItemFilterColor(muicolor.INFO);
    } else {
      setItemFilterColor(muicolor.DEFAULT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilterList]);

  const isReadyCondition = gridLoading && !isEmpty(rpVersionFilter) && !isEmpty(measures);

  useEffect(() => {
    if (reload && isReadyCondition) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    if (isReadyCondition) {
      retrievePlanAndActual();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, measures, sortParams]);

  useEffect(() => {
    if (isReadyCondition) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue.originLocation, filterValue.destLocation, filterValue.category, invClassCdList, advancedFilters]);

  useEffect(() => {
    if (reduxViewState?.rpVersion && isReadyCondition) {
      retreiveConfig(true);
    } else if (rpVersionFilter?.id && isUndefined(reduxViewState?.bucketType)) {
      setBucketType(rpVersionFilter?.planConfigObj?.BUCKET_UNIT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter?.id])

  useEffect(() => {
    retrieveHrchyGroup();
    retrieveCustomConfig();
    retrieveP13n();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const initCheck = params => {
    if (!params?.api || initSelectionDoneRef.current) {
      return;
    }

    if (params.api.getDisplayedRowCount() < 1) {
      setChartData([]);
      return;
    }

    initSelectionDoneRef.current = true;
    selectTopRowGroup(params.api);
  };

  const getDisplayText = (translationKey, originTxt) => {
    if (displayName != null && displayName[translationKey]) {
      return displayName[translationKey];
    }
    return t(originTxt);
  };

  const chartSeries = () => {
    const seriesList = [];
    let colorIdx = 0;
    for (const chartMeasure of allMeasure) {
      let series = {};
      if (chartMeasure !== ARRIVAL_QTY_GAP_PCT) {
        series = {
          field: chartMeasure,
          text:  getDisplayText(chartMeasure, chartMeasure),
          type: CodeUtil.toggleType.BAR,
          typeOptions: { fill: colorSet(colorIdx) },
          orientation: "left",
          formatter: numberUtil.formatNumber,
          groupName: t(QTY),
        }
      } else {
        series = {
          field: chartMeasure,
          text: getDisplayText(chartMeasure, chartMeasure),
          type: CodeUtil.toggleType.LINE,
          typeOptions: { fill: colorSet(colorIdx) },
          orientation: "right",
          formatter: num => `${num}%`,
          groupName: t("percent"),
        }
      }
      colorIdx++;
      seriesList.push(series);
    }
    return seriesList;
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    const wasAFActive = isAFActive;
    if (wasAFActive) {
      resetAdvancedFilter();
    }
    setOnlyExceptionApplied(value);

    const reduxState = reduxUtil.createObjectState(viewName, { onlyExceptionApplied: value, advancedFilters: null });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    if (!wasAFActive) {
      setReload(true);
    }
  }


  return (
    <>
      <FilterContainer>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={RELEASED}/>
        <LocationTreeFilter viewName={viewName} title={"originLocation"} filterValue={filterValue?.originLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <LocationTreeFilter viewName={viewName} title={"destLocation"} filterValue={filterValue?.destLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={handleFilterValueChange} treeload={treeload} customValue={itemHrchyClearValue} enableMultiSelect selectLeafs/>
        <IconButton
          aria-describedby={"prodHrchyPop"}
          size="small"
          sx={{ mr: 1 }}
          onClick={filterActionItem}
          color={itemFilterColor}>
          <Tooltip title={t("itemFilter")}>
            <FilterAlt />
          </Tooltip>
        </IconButton>
        <AbcXyzFilterModal invClassCdList={invClassCdList} setInvClassCdList={handleInvClassCdListChange} />
        <BucketToggleButton buckets={availableBuckets} value={bucketType} onChange={handleAlignment} key={"BUCKET"} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"onlyExceptionApplied"} checked={onlyExceptionApplied} onChange={handleCheckbox} />}
          label={t("exceptionsOnly")}
          key="DIFFERENCE_ONLY"
          sx={{ display: "inline-flex", minWidth: 110 }}
        />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          <ExcelExportButton
            title={title}
            service={rpPlanVsActualService}
            setExcelData={setGridDatas}
            columnDefs = {retrieveExportColumns()}
            totalRows={resultTotalCount.current * rowsPerCombination}
            params={{ ...getRequestParams(false), measureCount: rowsPerCombination, measures: isEmpty(measures) ? allMeasure : measures }}
            isCsv
            translationColumns={["measure"]}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={isAFActive ? muicolor.INFO : muicolor.DEFAULT} onClick={handleFilterOpen} />
        </Box>
      </FilterContainer>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <SplitterContainer collapseDirection="up" initialSizes={[2, 3]}>
            <Box className="chart-area">
              <ChartComposed
                // ChartComposed의 상태 업데이트를 강제로 적용
                key={chartData.length > 0 ? "data-present" : "data-empty"}
                xAxis={["yyyymmdd"]}
                data={chartData}
                dataSeries={chartSeries()}
                tooltipAnimation={false}
                activeMeasures={measures}
                // options={{ minHeight: "200px", isAmt: isEqual(measureType, AMT), symbol: isEqual(measureType, AMT) ? currencySymbol : "" }}
              />
            </Box>
            <Box sx={{ height: "100%" }}>
              <Box
                className="ag-theme-balham non-background-selected-row"
                mb={1}
                sx={{ height: "calc(100% - 60px)", mt: 2 }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={planAndActual}
                  onColumnVisible={onColumnVisible}
                  onColumnMoved={onColumnVisible}
                  {...defaultGridMemo}
                  groupDisplayType={'custom'}
                  groupDefaultExpanded={-1}
                  groupHideOpenParents
                  rowSelection={{
                    ...defaultGridMemo.rowSelection,
                    groupSelects: "descendants"
                  }}
                  statusBar={statusBar}
                  onGridReady={onGridReady}
                  onRowSelected={onRowSelected}
                  onCellClicked={onCellGridClicked}
                  onRowDataUpdated={initCheck}
                  onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
                />
              </Box>
              <PaginationContainer
                currentPage={currentPage} totalPages={gridPages}
                pageSize={pageSize} pageSizes={pageSizes}
                setCurrentPage={setCurrentPage} setPageSize={setPageSize}
              />
            </Box>
          </SplitterContainer>
        </Box>
        {openFilter && (
          <>
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={advancedFilterColumnDefs}
                advancedFilters={pendingAdvancedFilters ?? advancedFilters}
                onApply={handleAdvancedFilterApply}
                dataMap={itemGrpLevel?.length > 0 ? { levels: itemGrpLevel } : {}}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          </>
        )}
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        displayNames={displayName}
        hasDisplayName={true}
      />
      <RPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={item.name}
        modalResultData={setFilterData}
        filterTypes={{ Item: itemFilterType ?? [filterType.or.cd, filterType.equal.cd] }}
        filteredList={{ Item: itemFilterList ?? {} }}
      />
    </>
  );
};

export default withTranslation()(RpPlanVsActual);